import React from 'react'
import { Calendar, Footer } from '@pcln/date-picker'
import debounce from 'lodash/debounce'
import PropTypes from 'prop-types'
import { CustomDayWithPrice } from '..'
import CustomDayWithColorCode from './CustomDayWithColorCode/CustomDayWithColorCode'
import CustomFooterWithColorCode from './CustomFooterWithColorCode/CustomFooterWithColorCode'
import { pricePromise, processRecords } from './helper/helper'
import { getPricingData } from './getPricingData/getPricingData'
import { addMonths, parse, format } from 'date-fns'

const getLowestPrice = (priceMap) => Math.min(...Object.keys(priceMap).map((k) => priceMap[k].price))

const flightsPopulatedCheck = (flightsObj) => {
  const flights = flightsObj?.flights?.[0]
  return !!(flights?.departureAirport && flights?.arrivalAirport)
}

const getFooter = (onDismiss, useColorCode, lowestPrice, flightDetails) => {
  const showLegend = flightsPopulatedCheck(flightDetails)

  return useColorCode ? (
    <CustomFooterWithColorCode
      dismissCalendar={onDismiss}
      lowestPrice={lowestPrice}
      showLegend={showLegend}
    />
  ) : (
    <Footer dismissCalendar={onDismiss} />
  )
}

/** AirPricedCalendar */
export class AirPricedCalendar extends React.Component {
  static propTypes = {
    /** Object mapping arbitrary values to date keys */
    dayMap: PropTypes.object,
    /** One of 'DEFAULT' or 'EXPRESS'. Determines if prices should include SOPQ */
    variantName: PropTypes.string,
    /** Boolean for doubling pricing on RT calendar prices AIR-22601 */
    doubleCalendarPricing: PropTypes.bool,
    /** Flight details used to construct request for pricing info */
    flightDetails: PropTypes.shape({
      cabinClass: PropTypes.string,
      tripType: PropTypes.string,
      flights: PropTypes.arrayOf(
        PropTypes.shape({
          departureAirport: PropTypes.shape({
            id: PropTypes.string,
            cityCode: PropTypes.string,
          }).isRequired,
          arrivalAirport: PropTypes.shape({
            id: PropTypes.string,
            cityCode: PropTypes.string,
          }).isRequired,
        })
      ),
    }),
    /** Promise that returns pricing info */
    pricePromise: PropTypes.func,
    /** Callback when calendar selection changes */
    onChange: PropTypes.func,
    /** Show default footer provided by `@pcln/date-picker` */
    withFooter: PropTypes.bool,
    /** Callback when user dismisses calendar via ESC key or Done button */
    onDismiss: PropTypes.func,
    /** Boolean to display color code block based on price */
    useColorCode: PropTypes.bool,
  }

  static defaultProps = {
    dayMap: {},
    variantName: 'DEFAULT',
    flightDetails: {},
    pricePromise,
    doubleCalendarPricing: false,
  }

  constructor(props) {
    super(props)
    this.state = {
      dayMap: this.props.dayMap,
      lowestPrice: getLowestPrice(this.props.dayMap),
      useColorCode: this.props.useColorCode,
    }
    this.calendarProps = { ...this.props }

    // Delete props that are not passed to the date picker
    delete this.calendarProps.dayMap
    delete this.calendarProps.variantName
    delete this.calendarProps.flightDetails
    delete this.calendarProps.pricePromise
    delete this.calendarProps.onChange
    delete this.useColorCode

    this.debouncedPricing = debounce(this.getPricingData, 250, {
      trailing: true,
    })
    this.executedMonths = {}
    this.departDate = null
    this.displayMonths = {}
  }

  // componentDidMount implemented for
  // AIR-29152: AIR_HOME_CALENDAR_COLOR_RANGES
  componentDidMount() {
    // reusing useColorCode as it's already a boolean
    // check for AIR_HOME_CALENDAR_COLOR_RANGES variant
    const { useColorCode } = this.state

    if (this.calendarComponentRef && useColorCode && window?.innerHeight < 900) {
      this.calendarComponentRef.scrollIntoView({
        block: 'center',
      })
    }
  }

  onChange = (event, dates) => {
    this.departDate = dates.startDate || ''

    if (!dates.endDate) {
      this.executedMonths = {}
      this.getPricingData(this.displayMonths, true)
    }

    if (typeof this.props.onChange === 'function') {
      this.props.onChange(event, dates)
    }
  }

  getPricingData = (displayMonths, clearDayMap) => {
    this.displayMonths = displayMonths

    const { flightDetails, pricePromise, variantName } = this.props

    getPricingData(
      displayMonths,
      flightDetails,
      pricePromise,
      variantName,
      this.executedMonths,
      this.departDate
    )
      .then((apiRecords) => {
        if (!apiRecords) return

        const dayMap = processRecords(apiRecords, this.calendarProps.doubleCalendarPricing)

        const newState = {
          dayMap: clearDayMap ? { ...dayMap } : { ...dayMap, ...this.state.dayMap },
        }

        newState.lowestPrice = getLowestPrice(newState.dayMap)
        this.setState(newState)
      })
      .catch(() => {
        const { displayedFromMonth, displayedToMonth } = displayMonths
        let fromMonth = parse(displayedFromMonth, 'yyyyMM', new Date())
        const toMonth = parse(displayedToMonth, 'yyyyMM', new Date())
        while (fromMonth.valueOf() <= toMonth.valueOf()) {
          const fromMonthKey = format(fromMonth, 'yyyyMM')
          delete this.executedMonths[fromMonthKey]
          fromMonth = addMonths(fromMonth, 1)
        }
      })
  }

  renderDayFn = (props) => {
    const { day, isValid, isSelected, customDayProps, isGrouped } = props
    const CustomDay = this.state.useColorCode ? CustomDayWithColorCode : CustomDayWithPrice

    const showColor = flightsPopulatedCheck(this.props.flightDetails)

    return (
      <CustomDay
        day={day}
        isValid={isValid}
        isSelected={isSelected}
        customDayProps={customDayProps}
        isGrouped={isGrouped}
        lowestPrice={this.state.lowestPrice}
        showColor={showColor}
      />
    )
  }

  onDisplayMonthsChange = (displayMonths) => {
    this.debouncedPricing(displayMonths)
  }

  render() {
    const { dayMap, useColorCode } = this.state

    return (
      <Calendar
        setCalendarRef={(el) => {
          this.calendarComponentRef = el
        }}
        dayMap={dayMap}
        renderDayFn={this.renderDayFn}
        animateScroll
        onDisplayMonthsChange={this.onDisplayMonthsChange}
        onChange={this.onChange}
        dayWrapperStyleOverride={
          useColorCode && {
            display: 'inline-block',
            width: `calc(${100 / 7}% - 4px)`,
            margin: '2px',
          }
        }
        footer={
          (this.props.withFooter || useColorCode) &&
          getFooter(
            this.props.onDismiss,
            this.state.useColorCode,
            this.state.lowestPrice,
            this.props.flightDetails
          )
        }
        {...this.calendarProps}
      />
    )
  }
}

export default AirPricedCalendar
